import { useState } from 'react'
import { WhatIfScenario, MECETable } from '@models/index'
import { createBaselineScenario, createEmptyScenario, cloneScenario } from '@utils/scenarioCalculator'
import './ScenarioSelector.css'

interface ScenarioSelectorProps {
  scenarios: WhatIfScenario[]
  activeScenarioId: string | null
  meceTable: MECETable
  onSelectScenario: (scenarioId: string) => void
  onCreateScenario: (scenario: WhatIfScenario) => void
  onDeleteScenario: (scenarioId: string) => void
  onRenameScenario: (scenarioId: string, newName: string) => void
  onToggleComparison?: () => void
  comparisonMode?: boolean
}

export function ScenarioSelector({
  scenarios,
  activeScenarioId,
  meceTable,
  onSelectScenario,
  onCreateScenario,
  onDeleteScenario,
  onRenameScenario,
  onToggleComparison,
  comparisonMode = false
}: ScenarioSelectorProps) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newScenarioName, setNewScenarioName] = useState('')
  const [newScenarioDescription, setNewScenarioDescription] = useState('')
  const [createFromBaseline, setCreateFromBaseline] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')

  const handleCreateScenario = () => {
    if (!newScenarioName.trim()) return

    let newScenario: WhatIfScenario

    if (createFromBaseline) {
      // Clone the baseline or create a new baseline if none exists
      const baseline = scenarios.find(s => s.is_baseline)
      if (baseline) {
        newScenario = cloneScenario(baseline, newScenarioName.trim())
        newScenario.description = newScenarioDescription.trim() || undefined
      } else {
        newScenario = createBaselineScenario(meceTable, `scenario_${Date.now()}`)
        newScenario.name = newScenarioName.trim()
        newScenario.description = newScenarioDescription.trim() || undefined
        newScenario.is_baseline = false
      }
    } else {
      newScenario = createEmptyScenario(meceTable, newScenarioName.trim(), newScenarioDescription.trim() || undefined)
    }

    onCreateScenario(newScenario)
    setShowCreateModal(false)
    setNewScenarioName('')
    setNewScenarioDescription('')
    setCreateFromBaseline(true)
  }

  const handleStartEdit = (scenario: WhatIfScenario) => {
    setEditingId(scenario.id)
    setEditName(scenario.name)
  }

  const handleSaveEdit = () => {
    if (editingId && editName.trim()) {
      onRenameScenario(editingId, editName.trim())
    }
    setEditingId(null)
    setEditName('')
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditName('')
  }

  // Ensure baseline exists
  const hasBaseline = scenarios.some(s => s.is_baseline)

  return (
    <div className="scenario-selector">
      <div className="scenario-selector-header">
        <h4>Scenarios</h4>
        <div className="scenario-actions">
          {onToggleComparison && scenarios.length >= 2 && (
            <button
              className={`btn-compare ${comparisonMode ? 'active' : ''}`}
              onClick={onToggleComparison}
              title="Compare scenarios"
            >
              Compare
            </button>
          )}
          <button
            className="btn-create-scenario"
            onClick={() => setShowCreateModal(true)}
            title="Create new scenario"
          >
            + New
          </button>
        </div>
      </div>

      {/* Scenario List */}
      <div className="scenario-list">
        {!hasBaseline && (
          <button
            className="scenario-item baseline-prompt"
            onClick={() => {
              const baseline = createBaselineScenario(meceTable)
              onCreateScenario(baseline)
            }}
          >
            <span className="scenario-icon">+</span>
            <span className="scenario-info">
              <span className="scenario-name">Create Baseline</span>
              <span className="scenario-description">Start with all tactics included</span>
            </span>
          </button>
        )}

        {scenarios.map(scenario => {
          const isActive = scenario.id === activeScenarioId
          const isEditing = editingId === scenario.id

          return (
            <div
              key={scenario.id}
              className={`scenario-item ${isActive ? 'active' : ''} ${scenario.is_baseline ? 'baseline' : ''}`}
            >
              {isEditing ? (
                <div className="scenario-edit-form">
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveEdit()
                      if (e.key === 'Escape') handleCancelEdit()
                    }}
                    autoFocus
                  />
                  <button className="btn-save-edit" onClick={handleSaveEdit}>Save</button>
                  <button className="btn-cancel-edit" onClick={handleCancelEdit}>Cancel</button>
                </div>
              ) : (
                <>
                  <button
                    className="scenario-select-btn"
                    onClick={() => onSelectScenario(scenario.id)}
                  >
                    <span className="scenario-icon">
                      {scenario.is_baseline ? '📋' : '📝'}
                    </span>
                    <span className="scenario-info">
                      <span className="scenario-name">
                        {scenario.name}
                        {scenario.is_baseline && <span className="baseline-badge">Baseline</span>}
                      </span>
                      {scenario.description && (
                        <span className="scenario-description">{scenario.description}</span>
                      )}
                    </span>
                  </button>
                  <div className="scenario-item-actions">
                    <button
                      className="btn-edit-scenario"
                      onClick={() => handleStartEdit(scenario)}
                      title="Rename scenario"
                    >
                      ✎
                    </button>
                    {!scenario.is_baseline && (
                      <button
                        className="btn-delete-scenario"
                        onClick={() => onDeleteScenario(scenario.id)}
                        title="Delete scenario"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>

      {/* Create Scenario Modal */}
      {showCreateModal && (
        <div className="scenario-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="scenario-modal" onClick={e => e.stopPropagation()}>
            <h4>Create New Scenario</h4>

            <div className="form-group">
              <label htmlFor="scenario-name">Scenario Name</label>
              <input
                id="scenario-name"
                type="text"
                value={newScenarioName}
                onChange={e => setNewScenarioName(e.target.value)}
                placeholder="e.g., Budget-Constrained Plan"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="scenario-description">Description (optional)</label>
              <textarea
                id="scenario-description"
                value={newScenarioDescription}
                onChange={e => setNewScenarioDescription(e.target.value)}
                placeholder="What is this scenario exploring?"
                rows={2}
              />
            </div>

            <div className="form-group">
              <label>Starting Point</label>
              <div className="radio-options">
                <label className="radio-option">
                  <input
                    type="radio"
                    checked={createFromBaseline}
                    onChange={() => setCreateFromBaseline(true)}
                  />
                  <span>Copy from baseline (all included)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    checked={!createFromBaseline}
                    onChange={() => setCreateFromBaseline(false)}
                  />
                  <span>Start fresh (all pending)</span>
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="btn-create"
                onClick={handleCreateScenario}
                disabled={!newScenarioName.trim()}
              >
                Create Scenario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ScenarioSelector
